/**
 * ReportFilterDrawer — "Report Settings" drawer with accordion sections.
 *
 * Replaces V1's multi-tab modal with a modern accordion-based drawer (660px).
 * 6 accordion groups: Time Range + 5 filter groups (11 sub-sections).
 *
 * Props:
 *   open              — boolean
 *   onClose           — () => void
 *   onApply           — (filters) => void
 *   onClear           — () => void
 *   filterOptions     — all option datasets from fetchFilterOptions
 *   currentFilters    — current active filter selections
 *   saving            — boolean (loading state for apply button)
 *   timeRanges        — TIME_RANGES constant array
 *   rangeId           — currently selected range id
 *   customRange       — boolean: is custom range active?
 *   start             — Date object: start date
 *   end               — Date object: end date
 *   onTimeRangeChange — (range) => void
 *   onCustomToggle    — () => void
 *   onStartChange     — (date) => void
 *   onEndChange       — (date) => void
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// ---------------------------------------------------------------------------
// Accordion section definitions
// ---------------------------------------------------------------------------

const SECTIONS = [
    {
        id: 'areas',
        label: 'Areas',
        subs: [
            { key: 'areas', label: 'Area', idKey: 'area_id', nameKey: 'area_name', optionsKey: 'areas' },
            { key: 'areaGroups', label: 'Area Groups', idKey: 'area_group_id', nameKey: 'area_group_name', optionsKey: 'areaGroups' },
            { key: 'areaTypes', label: 'Area Types', idKey: 'area_type_id', nameKey: 'area_type_name', optionsKey: 'areaTypes' },
        ],
    },
    {
        id: 'residents',
        label: 'Residents',
        subs: [
            { key: 'residents', label: 'Resident', idKey: 'resident_id', nameKey: 'resident_name', optionsKey: 'residents' },
            { key: 'residentGroups', label: 'Resident Groups', idKey: 'resident_group_id', nameKey: 'resident_group_name', optionsKey: 'residentGroups' },
        ],
    },
    {
        id: 'users',
        label: 'Users',
        subs: [
            { key: 'users', label: 'User', idKey: 'user_id', nameKey: 'display_name', optionsKey: 'users' },
            { key: 'userGroups', label: 'User Groups', idKey: 'group_id', nameKey: 'group_name', optionsKey: 'userGroups' },
        ],
    },
    {
        id: 'devices',
        label: 'Devices',
        subs: [
            { key: 'deviceCategories', label: 'Device Categories', idKey: 'device_category_id', nameKey: 'device_category_short_name', optionsKey: 'deviceCategories' },
            { key: 'devices', label: 'Devices', idKey: 'device_id', nameKey: 'device_name', subtextKey: 'manufacture_uuid', subtextLabel: 'Serial', optionsKey: 'devices' },
        ],
    },
    {
        id: 'events',
        label: 'Events',
        subs: [
            { key: 'eventTypes', label: 'Event Types', idKey: 'event_type_id', nameKey: 'event_type_name', optionsKey: 'eventTypes' },
            { key: 'reasons', label: 'Reasons', idKey: 'reason_code_id', nameKey: 'reason_code_description', optionsKey: 'reasonCodes' },
        ],
    },
];

// ---------------------------------------------------------------------------
// Empty filter state factory
// ---------------------------------------------------------------------------

function emptyFilters() {
    return {
        areas: new Set(),
        areaGroups: new Set(),
        areaTypes: new Set(),
        residents: new Set(),
        residentGroups: new Set(),
        users: new Set(),
        userGroups: new Set(),
        deviceCategories: new Set(),
        devices: new Set(),
        eventTypes: new Set(),
        reasons: new Set(),
        responseTime: { enabled: false, operator: 'lt', value: 60 },
    };
}

function cloneFilters(f) {
    const result = {};
    Object.keys(f).forEach((k) => {
        if (k === 'responseTime') {
            result[k] = { ...f[k] };
        } else {
            result[k] = new Set(f[k]);
        }
    });
    return result;
}

function filtersToSets(currentFilters) {
    if (!currentFilters) return emptyFilters();
    const result = {};
    Object.keys(currentFilters).forEach((k) => {
        if (k === 'responseTime') {
            result[k] = { ...(currentFilters[k] || { enabled: false, operator: 'lt', value: 60 }) };
        } else {
            result[k] = new Set((currentFilters[k] || []).map(String));
        }
    });
    return result;
}

// ---------------------------------------------------------------------------
// Sub-section component — search + checkboxes
// ---------------------------------------------------------------------------

const FilterSubSection = ({ subDef, options, selected, onToggle, onSelectAll, onClear }) => {
    const [search, setSearch] = useState('');
    const [showSelectedOnly, setShowSelectedOnly] = useState(false);

    const hasSubtext = Boolean(subDef.subtextKey);

    const filteredOptions = useMemo(() => {
        let list = options || [];
        if (showSelectedOnly) {
            list = list.filter((opt) => selected.has(String(opt[subDef.idKey])));
        }
        if (search) {
            const term = search.toLowerCase();
            list = list.filter((opt) =>
                (opt[subDef.nameKey] || '').toLowerCase().includes(term) ||
                (hasSubtext && (opt[subDef.subtextKey] || '').toLowerCase().includes(term))
            );
        }
        return list;
    }, [options, search, subDef.idKey, subDef.nameKey, subDef.subtextKey, hasSubtext, selected, showSelectedOnly]);

    return (
        <div className="ar-filter-section">
            <div className="ar-filter-section__header">
                <span className="ar-filter-section__label">{subDef.label}</span>
                <div className="ar-filter-section__actions">
                    <button
                        className="ar-filter-section__btn"
                        onClick={() => onSelectAll(subDef.key, (options || []).map((o) => String(o[subDef.idKey])))}
                        type="button"
                    >
                        Select All
                    </button>
                    <button
                        className="ar-filter-section__btn"
                        onClick={() => onClear(subDef.key)}
                        type="button"
                    >
                        Clear
                    </button>
                    <button
                        className={`ar-filter-section__btn${showSelectedOnly ? ' ar-filter-section__btn--active' : ''}`}
                        onClick={() => setShowSelectedOnly((prev) => !prev)}
                        type="button"
                    >
                        Show Selected
                    </button>
                </div>
            </div>
            <input
                className="ar-filter-section__search"
                type="text"
                placeholder={`Search ${subDef.label.toLowerCase()}...`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
            />
            <div className="ar-filter-list">
                {filteredOptions.length === 0 ? (
                    <p className="ar-filter-list__empty">
                        {showSelectedOnly ? 'No selections.' : 'No options found.'}
                    </p>
                ) : null}
                {filteredOptions.map((opt) => {
                    const id = String(opt[subDef.idKey]);
                    const checked = selected.has(id);
                    const subtextVal = hasSubtext ? opt[subDef.subtextKey] : null;
                    return (
                        <label key={id} className={`ar-filter-list__item${hasSubtext ? ' ar-filter-list__item--has-subtext' : ''}`}>
                            <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => onToggle(subDef.key, id)}
                            />
                            {hasSubtext ? (
                                <span className="ar-filter-list__item-content">
                                    <span className="ar-filter-list__item-name">{opt[subDef.nameKey] || '(empty)'}</span>
                                    {subtextVal ? (
                                        <span className="ar-filter-list__item-subtext">
                                            {subDef.subtextLabel ? `${subDef.subtextLabel}: ` : ''}{subtextVal}
                                        </span>
                                    ) : null}
                                </span>
                            ) : (
                                <span>{opt[subDef.nameKey] || '(empty)'}</span>
                            )}
                        </label>
                    );
                })}
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ReportFilterDrawer = ({
    open,
    onClose,
    onApply,
    onClear,
    filterOptions,
    currentFilters,
    saving,
    // Time range props
    timeRanges,
    rangeId,
    customRange,
    start,
    end,
    onTimeRangeChange,
    onCustomToggle,
    onStartChange,
    onEndChange,
    // Optional overrides
    hideTimeRange = false,
    sections: sectionsProp,
    hideResponseTime = false,
    dateOnly = false,
}) => {
    const activeSections = sectionsProp || SECTIONS;
    // Working copy of filters
    const [working, setWorking] = useState(() => filtersToSets(currentFilters));
    const [expandedSections, setExpandedSections] = useState(new Set(['timeRange']));

    // Sync working copy when drawer opens or currentFilters change
    useEffect(() => {
        if (open) {
            setWorking(filtersToSets(currentFilters));
        }
    }, [open, currentFilters]);

    // Toggle accordion
    const toggleSection = useCallback((sectionId) => {
        setExpandedSections((prev) => {
            const next = new Set(prev);
            if (next.has(sectionId)) {
                next.delete(sectionId);
            } else {
                next.add(sectionId);
            }
            return next;
        });
    }, []);

    // Checkbox toggle
    const handleToggle = useCallback((filterKey, id) => {
        setWorking((prev) => {
            const next = cloneFilters(prev);
            if (next[filterKey].has(id)) {
                next[filterKey].delete(id);
            } else {
                next[filterKey].add(id);
            }
            return next;
        });
    }, []);

    // Select all
    const handleSelectAll = useCallback((filterKey, allIds) => {
        setWorking((prev) => {
            const next = cloneFilters(prev);
            next[filterKey] = new Set(allIds);
            return next;
        });
    }, []);

    // Clear section
    const handleClear = useCallback((filterKey) => {
        setWorking((prev) => {
            const next = cloneFilters(prev);
            next[filterKey] = new Set();
            return next;
        });
    }, []);

    // Response time handlers
    const handleRespToggle = useCallback((e) => {
        setWorking((prev) => {
            const next = cloneFilters(prev);
            next.responseTime = { ...next.responseTime, enabled: e.target.checked };
            return next;
        });
    }, []);

    const handleRespOperator = useCallback((e) => {
        setWorking((prev) => {
            const next = cloneFilters(prev);
            next.responseTime = { ...next.responseTime, operator: e.target.value };
            return next;
        });
    }, []);

    const handleRespValue = useCallback((e) => {
        setWorking((prev) => {
            const next = cloneFilters(prev);
            next.responseTime = { ...next.responseTime, value: parseInt(e.target.value, 10) || 0 };
            return next;
        });
    }, []);

    // Count selections for a section group
    const getSectionCount = useCallback((section) => {
        let count = 0;
        section.subs.forEach((sub) => {
            count += (working[sub.key]?.size || 0);
        });
        if (section.id === 'events' && working.responseTime?.enabled) {
            count += 1;
        }
        return count;
    }, [working]);

    // Apply filters
    const handleApply = useCallback(() => {
        // Convert Sets to arrays for the parent
        const result = {};
        Object.keys(working).forEach((k) => {
            if (k === 'responseTime') {
                result[k] = { ...working[k] };
            } else {
                result[k] = [...working[k]];
            }
        });
        onApply(result);
    }, [working, onApply]);

    // Clear all
    const handleClearAll = useCallback(() => {
        setWorking(emptyFilters());
        onClear();
    }, [onClear]);

    return (
        <>
            {open ? <div className="dp-drawer-backdrop" onClick={onClose} /> : null}
            <div className={`dp-drawer dp-drawer--xl${open ? ' is-open' : ''}`}>
                <div className="dp-drawer__header">
                    <h2 className="dp-drawer__title">Report Settings</h2>
                    <button className="dp-drawer__close" onClick={onClose}>{'\u2715'}</button>
                </div>

                <div className="dp-drawer__body" style={{ overflowY: 'auto', flex: 1 }}>
                    {/* Time Range — first accordion (changes take effect immediately) */}
                    {!hideTimeRange ? (
                    <div className="ar-accordion">
                        <button
                            className={`ar-accordion__header${expandedSections.has('timeRange') ? ' ar-accordion__header--open' : ''}`}
                            onClick={() => toggleSection('timeRange')}
                            type="button"
                        >
                            <span className="ar-accordion__icon">
                                {expandedSections.has('timeRange') ? '\u25BC' : '\u25B6'}
                            </span>
                            <span className="ar-accordion__label">Time Range</span>
                            <span className="ar-accordion__count">
                                {customRange ? 'Custom' : (timeRanges || []).find((r) => r.id === rangeId)?.label || 'Yesterday'}
                            </span>
                        </button>
                        {expandedSections.has('timeRange') ? (
                            <div className="ar-accordion__body">
                                <div className="ar-time-bar">
                                    <div className="ar-time-bar__ranges">
                                        {(timeRanges || []).map((range) => (
                                            <button
                                                key={range.id}
                                                className={`ar-time-btn${rangeId === range.id ? ' ar-time-btn--active' : ''}`}
                                                onClick={() => onTimeRangeChange(range)}
                                                type="button"
                                            >
                                                {range.label}
                                            </button>
                                        ))}
                                        <button
                                            className={`ar-time-btn${customRange ? ' ar-time-btn--active' : ''}`}
                                            onClick={onCustomToggle}
                                            type="button"
                                        >
                                            Custom
                                        </button>
                                    </div>
                                    {customRange ? (
                                        <div className="ar-custom-range">
                                            <DatePicker
                                                selected={start}
                                                onChange={(date) => onStartChange(date)}
                                                selectsStart
                                                startDate={start}
                                                endDate={end}
                                                maxDate={end}
                                                {...(!dateOnly && { showTimeSelect: true, timeIntervals: 15 })}
                                                dateFormat={dateOnly ? 'MM/dd/yyyy' : 'MM/dd/yyyy h:mm aa'}
                                                className="ar-custom-range__input"
                                                placeholderText={dateOnly ? 'Start date' : 'Start date/time'}
                                            />
                                            <span className="ar-custom-range__sep">to</span>
                                            <DatePicker
                                                selected={end}
                                                onChange={(date) => onEndChange(date)}
                                                selectsEnd
                                                startDate={start}
                                                endDate={end}
                                                minDate={start}
                                                {...(!dateOnly && { showTimeSelect: true, timeIntervals: 15 })}
                                                dateFormat={dateOnly ? 'MM/dd/yyyy' : 'MM/dd/yyyy h:mm aa'}
                                                className="ar-custom-range__input"
                                                placeholderText={dateOnly ? 'End date' : 'End date/time'}
                                            />
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        ) : null}
                    </div>
                    ) : null}

                    {activeSections.map((section) => {
                        const isExpanded = expandedSections.has(section.id);
                        const count = getSectionCount(section);
                        return (
                            <div key={section.id} className="ar-accordion">
                                <button
                                    className={`ar-accordion__header${isExpanded ? ' ar-accordion__header--open' : ''}`}
                                    onClick={() => toggleSection(section.id)}
                                    type="button"
                                >
                                    <span className="ar-accordion__icon">
                                        {isExpanded ? '\u25BC' : '\u25B6'}
                                    </span>
                                    <span className="ar-accordion__label">{section.label}</span>
                                    {count > 0 ? (
                                        <span className="ar-accordion__count">{count} selected</span>
                                    ) : null}
                                </button>
                                {isExpanded ? (
                                    <div className="ar-accordion__body">
                                        {section.subs.map((sub) => (
                                            <FilterSubSection
                                                key={sub.key}
                                                subDef={sub}
                                                options={filterOptions?.[sub.optionsKey] || []}
                                                selected={working[sub.key] || new Set()}
                                                onToggle={handleToggle}
                                                onSelectAll={handleSelectAll}
                                                onClear={handleClear}
                                            />
                                        ))}

                                        {/* Response Time — special section inside Events */}
                                        {section.id === 'events' && !hideResponseTime ? (
                                            <div className="ar-filter-section">
                                                <div className="ar-filter-section__header">
                                                    <span className="ar-filter-section__label">Response Time</span>
                                                </div>
                                                <div className="ar-resp-filter">
                                                    <label className="ar-resp-filter__toggle">
                                                        <input
                                                            type="checkbox"
                                                            checked={working.responseTime?.enabled || false}
                                                            onChange={handleRespToggle}
                                                        />
                                                        <span>Enable</span>
                                                    </label>
                                                    {working.responseTime?.enabled ? (
                                                        <div className="ar-resp-filter__controls">
                                                            <select
                                                                className="ar-resp-filter__select"
                                                                value={working.responseTime?.operator || 'lt'}
                                                                onChange={handleRespOperator}
                                                            >
                                                                <option value="lt">Less than</option>
                                                                <option value="gt">Greater than</option>
                                                                <option value="eq">Equal to</option>
                                                            </select>
                                                            <input
                                                                className="ar-resp-filter__input"
                                                                type="number"
                                                                value={working.responseTime?.value || 60}
                                                                onChange={handleRespValue}
                                                                min={0}
                                                            />
                                                            <span className="ar-resp-filter__unit">min</span>
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </div>
                                        ) : null}
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>

                <div className="dp-drawer__footer">
                    <button className="dp-btn dp-btn--cancel" onClick={handleClearAll} disabled={saving} type="button">
                        Clear All
                    </button>
                    <button className="dp-btn dp-btn--primary" onClick={handleApply} disabled={saving} type="button">
                        {saving ? 'Applying\u2026' : 'Apply Settings'}
                    </button>
                </div>
            </div>
        </>
    );
};

export default ReportFilterDrawer;
